import React from 'react';
import {
    Target,
    Users,
    Globe,
    Award,
    Package,
    TrendingUp,
    CheckCircle,
    Star,
    Truck,
    Building,
    MapPin,
    Phone,
    Mail,
    Calendar,
    Briefcase,
    Network,
    Shield,
    Clock,
    Plane
} from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const AboutPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-green-50">
            {/* Navigation */}
            <ResponsiveNavbar />

            {/* Hero Section */}
            <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
                        <Shield className="w-4 h-4 mr-2" />
                        About CFL Global Logistics
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Your Gateway to
                        <span className="bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent"> Seamless Logistics</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                        CFL Global Logistics Limited is a subsidiary of CFL Global Holdings and a proud member of Skynet Worldwide Express,
                        the largest independently owned express delivery network in the world.
                    </p>
                </div>
            </section>

            {/* Company Introduction */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Story</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Combining local expertise with global reach to deliver exceptional shipping solutions
                        </p>
                    </div>

                    <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-8 sm:p-12 text-white mb-16">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
                                <Globe className="w-10 h-10" />
                            </div>
                            <p className="text-xl sm:text-2xl leading-relaxed text-white">
                                As one of Nigeria's leading logistics companies, we combine local expertise with global reach to deliver
                                exceptional shipping solutions. Our partnership with Skynet Worldwide Express gives us access to an
                                unparalleled global network while maintaining the personal service and competitive pricing of a local Nigerian business.
                            </p>
                        </div>
                    </div>

                    {/* Mission & Vision */}
                    <div className="grid md:grid-cols-2 gap-8 mb-16">
                        <div className="bg-gradient-to-br from-gray-50 to-red-50 rounded-2xl p-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mb-6">
                                <Target className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To provide fast, reliable, and cost-effective logistics solutions that connect Nigerian businesses
                                and individuals to opportunities across Nigeria and around the world.
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl p-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center text-white mb-6">
                                <Star className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To be the most trusted logistics partner in Nigeria, known for innovation, reliability,
                                and exceptional customer service.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-green-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            The principles that guide everything we do and shape our commitment to excellence
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
                        {[
                            {
                                icon: <Shield className="w-8 h-8" />,
                                title: 'Reliability',
                                description: 'We deliver on our promises, every time',
                                color: 'from-green-500 to-green-600'
                            },
                            {
                                icon: <Target className="w-8 h-8" />,
                                title: 'Innovation',
                                description: 'Continuously improving our technology and processes',
                                color: 'from-blue-500 to-blue-600'
                            },
                            {
                                icon: <Users className="w-8 h-8" />,
                                title: 'Customer Focus',
                                description: 'Your success is our success',
                                color: 'from-red-500 to-red-600'
                            },
                            {
                                icon: <CheckCircle className="w-8 h-8" />,
                                title: 'Integrity',
                                description: 'Honest, transparent business practices',
                                color: 'from-purple-500 to-purple-600'
                            },
                            {
                                icon: <Award className="w-8 h-8" />,
                                title: 'Excellence',
                                description: 'Setting the highest standards in everything we do',
                                color: 'from-orange-500 to-orange-600'
                            }
                        ].map((value, index) => (
                            <div key={index} className="group p-6 rounded-2xl bg-white hover:shadow-lg transition-all duration-300">
                                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto`}>
                                    {value.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">{value.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-center text-sm">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Expertise */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Expertise</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Comprehensive logistics solutions backed by years of experience and industry expertise
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Package className="w-8 h-8" />,
                                title: 'Express Document & Parcel Delivery',
                                description: 'Same-day and next-day delivery with real-time tracking and secure handling protocols.'
                            },
                            {
                                icon: <Truck className="w-8 h-8" />,
                                title: 'Haulage & Heavy Cargo',
                                description: 'Complete truck fleet for all cargo sizes with professional loading and route optimization.'
                            },
                            {
                                icon: <Plane className="w-8 h-8" />,
                                title: 'International Shipping',
                                description: 'Global shipping via Skynet network with air freight, sea freight, and customs clearance.'
                            },
                            {
                                icon: <Shield className="w-8 h-8" />,
                                title: 'Customs Clearing Services',
                                description: 'Expert customs clearance with documentation preparation and regulatory compliance.'
                            },
                            {
                                icon: <Clock className="w-8 h-8" />,
                                title: 'Real-Time Tracking',
                                description: 'Advanced tracking technology providing live updates and delivery confirmations.'
                            },
                            {
                                icon: <Building className="w-8 h-8" />,
                                title: 'Specialized Services',
                                description: 'Repair & return services, GIT insurance, and tailored solutions for unique needs.'
                            }
                        ].map((expertise, index) => (
                            <div key={index} className="bg-gradient-to-br from-gray-50 to-red-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300 group">
                                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                                    {expertise.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                                    {expertise.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed text-sm">{expertise.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-green-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Leadership</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Experienced professionals dedicated to delivering exceptional logistics solutions
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="bg-white rounded-2xl p-8 shadow-sm">
                            <div className="text-center">
                                <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <span className="text-white font-bold text-2xl">OR</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Oluwaseun Raji-Awe</h3>
                                <p className="text-red-600 font-semibold mb-4">General Manager</p>
                                <p className="text-gray-600 leading-relaxed">
                                    Leading CFL Global Logistics with years of experience in the logistics and transportation industry.
                                    Committed to delivering excellence and building lasting partnerships with clients across Nigeria and globally.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Company Stats */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-600 to-red-700">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Impact</h2>
                        <p className="text-xl text-red-100 max-w-2xl mx-auto">
                            Delivering measurable results and creating lasting impact for our clients across Nigeria and beyond
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { number: '5+', label: 'Years of Excellence', icon: <Calendar className="w-8 h-8" /> },
                            { number: '500+', label: 'Daily Deliveries', icon: <Package className="w-8 h-8" /> },
                            { number: '36', label: 'States Covered', icon: <MapPin className="w-8 h-8" /> },
                            { number: '99.8%', label: 'On-Time Delivery', icon: <TrendingUp className="w-8 h-8" /> }
                        ].map((stat, index) => (
                            <div key={index} className="text-center group">
                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    {stat.icon}
                                </div>
                                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                                <div className="text-red-100">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose CFL Global?</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            We are equipped with the expertise and technology to handle any logistics challenge
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Unmatched Reliability & Service</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Our team of logistics professionals brings years of experience and expertise to every shipment.
                                We are equipped with state-of-the-art technology and a comprehensive network to ensure your cargo
                                reaches its destination safely and on time.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    'Member of Skynet Worldwide Express network',
                                    'Real-time tracking and notifications',
                                    'Comprehensive GIT insurance coverage',
                                    'Competitive rates with volume discounts',
                                    'Nationwide reach covering all 36 states',
                                    'Professional customer service support'
                                ].map((point, index) => (
                                    <li key={index} className="flex items-center">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                        <span className="text-gray-700">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-red-50 to-green-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Global Network, Local Expertise</h3>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <Globe className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Skynet Partnership</h4>
                                        <p className="text-gray-600 text-sm">Access to the world's largest independent express delivery network</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <MapPin className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Local Expertise</h4>
                                        <p className="text-gray-600 text-sm">Deep understanding of Nigerian market dynamics and regulations</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <Network className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Extensive Network</h4>
                                        <p className="text-gray-600 text-sm">Comprehensive coverage across Nigeria with reliable delivery partners</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-green-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                        Ready to Experience Reliable Logistics?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Join thousands of satisfied customers who trust CFL Global Logistics for their shipping needs across Nigeria and worldwide.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href='/quote' className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg">
                            Get Instant Quote
                        </a>
                        <a href='/contact-us' className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-red-500 hover:text-red-600 transition-all duration-300">
                            Contact Our Team
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <div className="flex items-center space-x-2 mb-6">
                                <a href='/' className="flex items-center space-x-2">
                                    <img
                                        src="logo.png"
                                        alt="CFL Global Logistics"
                                        className="w-50 h-12 rounded-xl object-contain"
                                    />
                                    <span className="text-xl font-bold text-white">CFL Global</span>
                                </a>

                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Your trusted logistics partner in Nigeria, connected to the world through the Skynet Worldwide Express network.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Services</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li>Express Document Delivery</li>
                                <li>Parcel & Package Services</li>
                                <li>Haulage & Heavy Cargo</li>
                                <li>International Shipping</li>
                                <li>Customs Clearing</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Quick Links</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="/tracking" className="hover:text-white transition-colors">Track Package</a></li>
                                <li><a href="/quote" className="hover:text-white transition-colors">Get Quote</a></li>
                                <li><a href="/services" className="hover:text-white transition-colors">Our Services</a></li>
                                <li><a href="/contact-us" className="hover:text-white transition-colors">Contact Us</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Contact Info</h4>
                            <div className="space-y-3 text-gray-400">
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 mr-2" />
                                    <div>
                                        <div>08023693696</div>
                                        <div>08037921992</div>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <Clock className="w-5 h-5 mr-2" />
                                    <div className="text-sm">
                                        <div>Mon-Fri: 8:00 AM - 6:00 PM</div>
                                        <div>Sat: 9:00 AM - 4:00 PM</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
                        <p>&copy; 2025 CFL Global Logistics Limited. All rights reserved. | A member of Skynet Worldwide Express</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;